/**
 * Phase G+ Phase 9b (Feb 2026) — Mitarbeiter-Profil "Akonto / Bonus" Tab
 * --------------------------------------------------------------------
 * Zwei klar getrennte Konzepte nebeneinander:
 *   - LINKS (rot):  Abzüge / Akonti / Vorschüsse — werden vom Lohn abgezogen.
 *   - RECHTS (grün): Boni / Prämien / Zulagen — werden zum Lohn dazugezählt.
 *
 * Beide Spalten unterstützen "Einmalig" (in einem Monat) ODER "Raten"
 * (mehrere Monatsraten) und nutzen denselben Backend-Endpunkt
 * `/api/advances` mit dem Feld `direction`.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtCur } from "@/lib/format";
import { Plus, X as XIcon, Loader2, Trash2, MinusCircle, PlusCircle, Ban } from "lucide-react";

const STATUS_LABEL = {
  active: "Aktiv",
  completed: "Abgeschlossen",
  cancelled: "Storniert",
};

const STATUS_TONE = {
  active: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
};

const todayYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function fmtMonth(ym) {
  if (!ym || ym.length !== 7) return "-";
  const [y, m] = ym.split("-").map(Number);
  return `${["Jän", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"][m - 1]} ${y}`;
}

function remainingOf(adv) {
  if (adv.status !== "active") return 0;
  if (adv.kind === "one_time") return Number(adv.total_amount || 0);
  const rem = Math.max(0, (adv.installments_total || 0) - (adv.installments_paid || 0));
  return rem * Number(adv.installment_amount || 0);
}

export default function EmployeeAdvancesTab({ empId, emp, canEdit }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dialogDir, setDialogDir] = useState(null); // "deduction" | "bonus" | null

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/advances/employee/${empId}/summary`);
      setSummary(data);
    } catch (e) {
      toast.error("Fehler beim Laden der Akonto-/Bonus-Daten");
    } finally {
      setLoading(false);
    }
  }, [empId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !summary) {
    return (
      <div className="flex items-center gap-2 text-zinc-400 py-12 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt …
      </div>
    );
  }

  const all = summary.items || [];
  const deductions = all.filter((a) => (a.direction || "deduction") === "deduction");
  const bonuses = all.filter((a) => a.direction === "bonus");

  return (
    <div className="space-y-4" data-testid="advances-tab-root">
      <div className="text-xs text-zinc-500">
        Verwalte hier <span className="text-rose-300 font-medium">Abzüge</span> (Akonti, Vorschüsse, die monatlich vom Lohn abgezogen werden) und{" "}
        <span className="text-emerald-300 font-medium">Boni</span> (Prämien, Zulagen, die dem Lohn dazugerechnet werden) für{" "}
        <span className="text-zinc-300">{emp?.first_name} {emp?.last_name}</span>.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ColumnCard
          tone="deduction"
          title="Abzüge / Akonti"
          icon={MinusCircle}
          subtitle="Werden vom Lohn abgezogen"
          openSum={summary.open_balance_deduction_eur || 0}
          activeCount={summary.active_deduction_count || 0}
          items={deductions}
          canEdit={canEdit}
          onAdd={() => setDialogDir("deduction")}
          onChanged={reload}
        />
        <ColumnCard
          tone="bonus"
          title="Boni / Prämien"
          icon={PlusCircle}
          subtitle="Werden dem Lohn dazugerechnet"
          openSum={summary.open_balance_bonus_eur || 0}
          activeCount={summary.active_bonus_count || 0}
          items={bonuses}
          canEdit={canEdit}
          onAdd={() => setDialogDir("bonus")}
          onChanged={reload}
        />
      </div>

      {dialogDir && (
        <AdvanceDialog
          empId={empId}
          direction={dialogDir}
          onClose={() => setDialogDir(null)}
          onSaved={() => { setDialogDir(null); reload(); }}
        />
      )}
    </div>
  );
}

function ColumnCard({ tone, title, icon: Icon, subtitle, openSum, activeCount, items, canEdit, onAdd, onChanged }) {
  const isDed = tone === "deduction";
  const border = isDed ? "border-rose-500/40" : "border-emerald-500/40";
  const bgHead = isDed ? "bg-rose-500/10" : "bg-emerald-500/10";
  const txtHead = isDed ? "text-rose-200" : "text-emerald-200";
  const accent = isDed ? "text-rose-300" : "text-emerald-300";
  const btnCls = isDed
    ? "bg-rose-600 hover:bg-rose-500 text-white"
    : "bg-emerald-600 hover:bg-emerald-500 text-white";

  return (
    <div className={`border ${border} bg-zinc-900/40 rounded-lg overflow-hidden`} data-testid={`advances-col-${tone}`}>
      <div className={`${bgHead} px-4 py-3 flex items-center justify-between border-b ${border}`}>
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${accent}`} />
          <div>
            <div className={`text-sm font-semibold ${txtHead}`}>{title}</div>
            <div className="text-[11px] text-zinc-500">{subtitle}</div>
          </div>
        </div>
        {canEdit && (
          <button
            onClick={onAdd}
            data-testid={`advances-add-${tone}-btn`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded ${btnCls}`}
          >
            <Plus className="w-3.5 h-3.5" />
            {isDed ? "Abzug anlegen" : "Bonus anlegen"}
          </button>
        )}
      </div>

      <div className="px-4 py-3 flex items-center gap-6 border-b border-zinc-800">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Offene Summe</div>
          <div className={`text-xl font-bold ${accent}`} data-testid={`advances-open-${tone}`}>
            {fmtCur(openSum)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Aktive Einträge</div>
          <div className="text-xl font-bold text-zinc-200">{activeCount}</div>
        </div>
      </div>

      <div className="divide-y divide-zinc-800">
        {items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-zinc-500">
            Keine Einträge vorhanden.
          </div>
        )}
        {items.map((adv) => (
          <Row key={adv.id} adv={adv} tone={tone} canEdit={canEdit} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function Row({ adv, tone, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isDed = tone === "deduction";
  const accent = isDed ? "text-rose-300" : "text-emerald-300";
  const rem = remainingOf(adv);

  const cancel = async () => {
    if (!window.confirm("Eintrag wirklich stornieren? Künftige Lohnläufe nutzen ihn dann nicht mehr.")) return;
    setBusy(true);
    try {
      await api.put(`/advances/${adv.id}`, { status: "cancelled" });
      toast.success("Storniert");
      onChanged?.();
    } catch (e) {
      toast.error("Stornierung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Eintrag endgültig löschen? Das kann nicht rückgängig gemacht werden.")) return;
    setBusy(true);
    try {
      await api.delete(`/advances/${adv.id}`);
      toast.success("Gelöscht");
      onChanged?.();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const kindLabel = adv.kind === "one_time"
    ? "Einmalig"
    : `Raten (${adv.installments_paid || 0}/${adv.installments_total || 0})`;

  return (
    <div className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`advances-row-${adv.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${accent}`}>{fmtCur(adv.total_amount)}</span>
          <span className="text-[11px] text-zinc-500">·</span>
          <span className="text-xs text-zinc-300">{kindLabel}</span>
          <span className="text-[11px] text-zinc-500">·</span>
          <span className="text-xs text-zinc-400">ab {fmtMonth(adv.start_month)}</span>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_TONE[adv.status] || ""}`}>
            {STATUS_LABEL[adv.status] || adv.status}
          </span>
        </div>
        {adv.reason && (
          <div className="text-xs text-zinc-500 mt-1 truncate">{adv.reason}</div>
        )}
        {adv.kind !== "one_time" && (
          <div className="text-[11px] text-zinc-500 mt-1">
            Monatsrate {fmtCur(adv.installment_amount)} · Rest {fmtCur(rem)}
          </div>
        )}
      </div>

      {canEdit && (
        <div className="flex items-center gap-1 shrink-0">
          {adv.status === "active" && (
            <button
              onClick={cancel}
              disabled={busy}
              data-testid={`advances-cancel-${adv.id}`}
              title="Stornieren"
              className="p-1.5 text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10 rounded"
            >
              <Ban className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={del}
            disabled={busy}
            data-testid={`advances-delete-${adv.id}`}
            title="Löschen"
            className="p-1.5 text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function AdvanceDialog({ empId, direction, onClose, onSaved }) {
  const isDed = direction === "deduction";
  const [kind, setKind] = useState("one_time");
  const [totalAmount, setTotalAmount] = useState("");
  const [installmentsTotal, setInstallmentsTotal] = useState(3);
  const [installmentAmount, setInstallmentAmount] = useState("");
  const [startMonth, setStartMonth] = useState(todayYM());
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const title = isDed ? "Neuer Abzug / Akonto" : "Neuer Bonus";
  const accentBtn = isDed
    ? "bg-rose-600 hover:bg-rose-500"
    : "bg-emerald-600 hover:bg-emerald-500";

  const submit = async () => {
    const t = Number(totalAmount);
    if (!t || t <= 0) { toast.error("Betrag muss > 0 sein"); return; }
    if (kind === "monthly_installments") {
      const n = Number(installmentsTotal);
      if (!n || n < 2) { toast.error("Mindestens 2 Raten"); return; }
    }
    setSaving(true);
    try {
      const body = {
        employee_id: empId,
        kind,
        direction,
        total_amount: t,
        start_month: startMonth,
        reason: reason || null,
      };
      if (kind === "monthly_installments") {
        body.installments_total = Number(installmentsTotal);
        if (installmentAmount) body.installment_amount = Number(installmentAmount);
      }
      await api.post("/advances", body);
      toast.success(isDed ? "Abzug angelegt" : "Bonus angelegt");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid={`advances-dialog-${direction}`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-zinc-100">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Art</label>
            <div className="flex gap-2 mt-1">
              {[["one_time", "Einmalig"], ["monthly_installments", "Raten"]].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setKind(v)}
                  data-testid={`advances-kind-${v}`}
                  className={`px-3 py-1.5 text-xs rounded border ${
                    kind === v
                      ? "bg-zinc-50 text-zinc-900 border-zinc-50"
                      : "bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-zinc-500"
                  }`}
                >{l}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Gesamtbetrag (€)</label>
            <input
              type="number" step="0.01" min="0"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              data-testid="advances-total-input"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
              placeholder="z. B. 500.00"
            />
          </div>

          {kind === "monthly_installments" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500">Anzahl Raten</label>
                <input
                  type="number" min="2"
                  value={installmentsTotal}
                  onChange={(e) => setInstallmentsTotal(e.target.value)}
                  data-testid="advances-installments-total"
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-zinc-500">Rate / Monat (optional)</label>
                <input
                  type="number" step="0.01" min="0"
                  value={installmentAmount}
                  onChange={(e) => setInstallmentAmount(e.target.value)}
                  data-testid="advances-installment-amount"
                  placeholder="autom."
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Startmonat</label>
            <input
              type="month"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
              data-testid="advances-start-month"
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Grund (optional)</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="advances-reason"
              placeholder={isDed ? "z. B. Lohnvorschuss Februar" : "z. B. Treue-Prämie"}
              className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-zinc-300 hover:text-zinc-100 rounded"
          >Abbrechen</button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="advances-save-btn"
            className={`px-4 py-2 text-sm text-white rounded font-medium ${accentBtn} disabled:opacity-50 inline-flex items-center gap-2`}
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}
